// Staging: where each character of a cartoon stands, which way it faces and what it does at every moment of the story. Pure functions.
//
// A screenplay is { cast: [{ id, name, kind, ... }], scenes: [{ setting, beats: [...] }] }, a beat being one thing that happens:
//   { speaker: id | null, text, action, emotion, moves: [{ id, from, to }], actions: { id: action }, seconds, narration_seconds }
// `from` / `to` of a move: "left" | "right" | "center" | "home" | "exit-left" | "exit-right" | "offscreen-left" | "offscreen-right" | a number (0..1 across the stage).
import { ACTIONS, MOVING, blendPoses, poseAt } from "./rig";

export const STAGE_X = { left: 0.14, right: 0.86, center: 0.5, "offscreen-left": -0.16, "offscreen-right": 1.16, "exit-left": -0.16, "exit-right": 1.16 };
const BLEND_SECONDS = 0.3;
const WALK_SHARE = 0.72;                               // a character walks for this share of a beat, and then stands where it arrived
const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const ease = (x) => { const t = clamp(x); return t * t * (3 - 2 * t); };

// Where the cast member number `index` of `count` stands by default.
export const homeX = (index, count) => (count <= 1 ? 0.5 : 0.2 + (0.6 * index) / (count - 1));

// How long a beat lasts: as long as it is spoken (and a breath), or the length it was given, or an estimate from its words.
export function beatSeconds(beat) {
  if (beat.narration_seconds) return Math.max(1.4, beat.narration_seconds + 0.35);
  if (beat.seconds) return Math.max(1, beat.seconds);
  const words = String(beat.text || "").split(/\s+/).filter(Boolean).length;
  if (words) return Math.max(1.6, words / 2.5 + 0.7);
  return (beat.moves || []).length ? 2.6 : 2.2;
}

const targetX = (to, home) => (typeof to === "number" ? to : to === "home" || to === undefined ? home : STAGE_X[to] ?? home);

// The whole story as a list of staged beats: each says, for every character in its scene, where it starts and ends, whether it is on stage, and what it does.
export function stageStory(screenplay) {
  const cast = (screenplay.cast || []).filter((c) => c.kind !== "narrator");
  const known = new Set(cast.map((c) => c.id));
  const staged = [];
  let sceneNo = 0;
  (screenplay.scenes || []).forEach((scene) => {
    const beats = scene.beats || [];
    // everybody the scene talks about is there from its start, unless their first appearance is an entrance
    const present = new Set();
    beats.forEach((b) => { if (b.speaker && known.has(b.speaker)) present.add(b.speaker); Object.keys(b.actions || {}).forEach((id) => known.has(id) && present.add(id)); (b.moves || []).forEach((m) => known.has(m.id) && present.add(m.id)); });
    const inScene = cast.filter((c) => present.has(c.id));                     // the characters of this scene, in the order of the cast, share the stage
    const homes = Object.fromEntries(inScene.map((c, i) => [c.id, homeX(i, inScene.length)]));
    const state = {};
    present.forEach((id) => { state[id] = { x: homes[id], visible: true, action: "idle", facing: homes[id] < 0.5 ? 1 : -1 }; });
    beats.forEach((beat, beatNo) => {
      const first = beat.moves?.find((m) => state[m.id] && String(m.from || "").startsWith("offscreen"));
      (beat.moves || []).forEach((m) => { if (state[m.id] && m.from !== undefined && m.from !== "home") { state[m.id].x = targetX(m.from, homes[m.id]); state[m.id].visible = true; } });
      const chars = {};
      const speakerX = beat.speaker && state[beat.speaker] ? state[beat.speaker].x : null;
      Object.keys(state).forEach((id) => {
        const s = state[id];
        const move = (beat.moves || []).find((m) => m.id === id);
        const x0 = s.x;
        const x1 = move ? targetX(move.to, homes[id]) : s.x;
        const exits = move && String(move.to).startsWith("exit");
        const moving = Math.abs(x1 - x0) > 0.02;
        let rest = beat.actions?.[id] || (id === beat.speaker ? "talk" : "idle");
        if (id === beat.speaker && !beat.actions?.[id] && beat.action && ACTIONS[beat.action] && !MOVING.has(beat.action)) rest = beat.action;
        if (!beat.speaker && !beat.actions?.[id] && beat.action && ACTIONS[beat.action] && !MOVING.has(beat.action)) rest = beat.action;
        if (MOVING.has(rest)) rest = id === beat.speaker ? "talk" : "idle";
        let action = rest;
        if (moving) action = (beat.actions?.[id] === "run" || beat.action === "run" || (move && move.how === "run")) ? "run" : "walk";
        let facing = s.facing;
        if (moving) facing = x1 > x0 ? 1 : -1;
        else if (speakerX !== null && id !== beat.speaker) facing = speakerX >= x0 ? 1 : -1;
        else if (id === beat.speaker) {
          const others = Object.keys(state).filter((o) => o !== id && state[o].visible);
          if (others.length) { const nearest = others.reduce((a, b) => (Math.abs(state[a].x - x0) < Math.abs(state[b].x - x0) ? a : b)); facing = state[nearest].x >= x0 ? 1 : -1; }
        }
        chars[id] = { visible: s.visible, x0, x1, facing, action, rest, prevAction: s.action, emotion: id === beat.speaker || beat.emotionAll ? beat.emotion || null : null, speaking: id === beat.speaker, exits: !!exits, entering: !!first && first.id === id };
        s.x = x1;
        s.facing = facing;
        s.action = rest;
        if (exits) s.visible = false;
      });
      staged.push({ scene: sceneNo, beat: beatNo, setting: scene.setting || "park", time: scene.time || "day", seconds: beatSeconds(beat), chars, speaker: beat.speaker || null, text: beat.text || "", sceneStart: beatNo === 0, source: beat });
    });
    sceneNo += 1;
  });
  return staged;
}

// One character at moment `t` (seconds into its beat): position, facing and the pose of every joint. `speech` = lipsync.mouthAt() of the line being spoken.
export function characterAt(beat, id, t, speech) {
  const c = beat.chars[id];
  if (!c) return null;
  const walkSpan = Math.max(0.5, beat.seconds * WALK_SHARE);
  const progress = ease(t / walkSpan);
  const x = c.x0 + (c.x1 - c.x0) * progress;
  const moving = Math.abs(c.x1 - c.x0) > 0.02 && t < walkSpan;
  const action = moving || !MOVING.has(c.action) ? c.action : c.rest;
  const travelled = Math.abs(c.x1 - c.x0) * progress;
  const phase = travelled * (action === "run" ? 46 : 38);                       // distance based: the feet keep to the ground
  const idx = id.length + id.charCodeAt(id.length - 1);
  const noSpeech = { energy: 0, nod: 0 };
  let pose = poseAt(action, t, c.speaking ? speech : noSpeech, phase, c.emotion, idx);
  if (t < BLEND_SECONDS && c.prevAction && c.prevAction !== action) {
    const before = poseAt(c.prevAction, beat.seconds + t, noSpeech, phase, null, idx);
    pose = blendPoses(before, pose, t / BLEND_SECONDS);
  }
  const speaking = c.speaking && speech ? speech.open : 0;
  pose.mouth = Math.max(pose.mouth || 0, speaking || 0);
  return { x, facing: c.facing, action, pose, visible: c.visible, speaking: c.speaking, wide: c.speaking && speech ? speech.wide : 0, round: c.speaking && speech ? speech.round : 0, exits: c.exits, progress };
}
